import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Card, Dropdown_, PageSidebar, PageTitle } from 'monica-alexandria';
import { useTranslation } from 'react-i18next';
import { Toolbar_ } from '../../partials/navigation/Toolbar_';
import { get_route_language } from '../../../helpers/routeLanguage';
import { list_goals } from '../../../services/api/Offers';

const GoalsContent = styled.div`
    display: grid;
    gap: 1.2rem;
`;

const GoalsTitleActions = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    min-width: 24rem;
`;

const GoalsSummary = styled(Card)`
    display: grid;
    gap: 0.4rem;
    padding: var(--normalPads);
    border: 0.1rem solid ${p => p.theme.low};

    h2,
    h5,
    p {
        margin: 0;
    }

    p {
        color: ${p => p.theme.high};
    }
`;

const GoalsTableWrap = styled(Card)`
    padding: 0;
    overflow: auto;
    border: 0.1rem solid ${p => p.theme.low};
`;

const GoalsTable = styled.table`
    width: 100%;
    min-width: 96rem;
    border-collapse: collapse;

    th,
    td {
        padding: 1rem;
        border-bottom: 0.1rem solid ${p => p.theme.mid};
        text-align: left;
        vertical-align: top;
    }

    th {
        position: sticky;
        top: 0;
        z-index: 1;
        background: ${p => p.theme.low};
    }

    tbody tr:hover {
        background: ${p => p.theme.low};
    }

    h5,
    h6,
    p {
        margin: 0;
    }
`;

const GoalsEmpty = styled(Card)`
    padding: var(--normalPads);
    background: ${p => p.theme.low};

    p {
        margin: 0;
    }
`;

const goals_request_params = (monthValue = '') => {
    const cleanMonthValue = `${monthValue || ''}`.trim();

    if (!cleanMonthValue.length) {
        return {};
    }

    return {
        month: cleanMonthValue
    };
};

const format_money = (amountValue = 0, currency = 'EUR') => {
    try {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currency || 'EUR'
        }).format(Number(amountValue || 0));
    } catch (error) {
        return `${Number(amountValue || 0).toFixed(2)} ${currency || 'EUR'}`;
    }
};

const format_date_time = (dateValue = '') => {
    const timeValue = new Date(dateValue).getTime();

    if (Number.isNaN(timeValue)) {
        return '-';
    }

    return new Intl.DateTimeFormat('en-GB', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
    }).format(new Date(timeValue));
};

const payment_type_label = (paymentType = '') => {
    if (paymentType === 'one_off') {
        return 'One-off';
    }

    return 'Recurring';
};

const payment_stage_label = (paymentStage = '') => {
    if (paymentStage === 'prepayment') {
        return 'Prepayment';
    }

    if (paymentStage === 'fulfillment') {
        return 'Fulfillment';
    }

    return 'Full payment';
};

const source_label = (source = '') => {
    if (source === 'agency') {
        return 'Agency';
    }

    return 'Legacy';
};

export default function GOALS() {
    const { t, i18n } = useTranslation();
    const [sidebar, setSidebar] = useState(false);
    const [goalsData, setGoalsData] = useState({
        months: [],
        rows: [],
        selectedMonth: '',
        totalAmountNet: 0,
        currency: 'EUR'
    });
    const [selectedMonth, setSelectedMonth] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState('');
    const routeLanguage = get_route_language(i18n.language);

    const get_goals = async(monthValue = '') => {
        setIsLoading(true);
        setErrorMessage('');

        try {
            const response = await list_goals(goals_request_params(monthValue));
            const nextGoalsData = response?.data?.data || {};

            setGoalsData({
                months: nextGoalsData.months || [],
                rows: nextGoalsData.rows || [],
                selectedMonth: nextGoalsData.selectedMonth || '',
                totalAmountNet: nextGoalsData.totalAmountNet || 0,
                currency: nextGoalsData.currency || 'EUR'
            });
            setSelectedMonth(nextGoalsData.selectedMonth || monthValue || '');
        } catch (error) {
            setErrorMessage(t('Could not load goals.'));
            setGoalsData({
                months: [],
                rows: [],
                selectedMonth: '',
                totalAmountNet: 0,
                currency: 'EUR'
            });
        } finally {
            setIsLoading(false);
        }
    };

    const change_month = (event) => {
        get_goals(event.target.value);
    };

    useEffect(() => {
        get_goals();
    }, []);

    return (
        <PageSidebar>
            <Toolbar_
                sidebar={sidebar}
                onSidebar={() => setSidebar(!sidebar)}
                routeLanguage={routeLanguage}
                activeOption="goals"
            />
            <GoalsContent>
                <PageTitle cta>
                    <h1>{t('Goals')}</h1>
                    <GoalsTitleActions>
                        <Dropdown_>
                            <select value={selectedMonth} onChange={change_month} disabled={!goalsData.months.length}>
                                {!goalsData.months.length ? <option value="">{t('No paid months')}</option> : null}
                                {goalsData.months.map((monthItem) => (
                                    <option key={monthItem.value} value={monthItem.value}>
                                        {monthItem.label}
                                    </option>
                                ))}
                            </select>
                        </Dropdown_>
                    </GoalsTitleActions>
                </PageTitle>
                {errorMessage ? <p>{errorMessage}</p> : null}
                {isLoading ? <p>{t('Loading goals...')}</p> : null}
                <GoalsSummary>
                    <p>{t('Total money generated')}</p>
                    <h2>{format_money(goalsData.totalAmountNet, goalsData.currency)}</h2>
                    <h5>{t('Included payments')}: {goalsData.rows.length}</h5>
                </GoalsSummary>
                {!isLoading && !goalsData.rows.length ? (
                    <GoalsEmpty>
                        <p>{t('No included payments for this month.')}</p>
                    </GoalsEmpty>
                ) : null}
                {goalsData.rows.length ? (
                    <GoalsTableWrap>
                        <GoalsTable>
                            <thead>
                                <tr>
                                    <th><h6>{t('Offer')}</h6></th>
                                    <th><h6>{t('Client')}</h6></th>
                                    <th><h6>{t('Source')}</h6></th>
                                    <th><h6>{t('Payment')}</h6></th>
                                    <th><h6>{t('Paid at')}</h6></th>
                                    <th><h6>{t('Amount')}</h6></th>
                                </tr>
                            </thead>
                            <tbody>
                                {goalsData.rows.map((rowItem) => (
                                    <tr key={rowItem.rowId}>
                                        <td><h5>{rowItem.ofrId || rowItem.offerId || '-'}</h5></td>
                                        <td><p>{rowItem.clientName || '-'}</p></td>
                                        <td><p>{t(source_label(rowItem.source))}</p></td>
                                        <td>
                                            <p>{t(payment_type_label(rowItem.paymentType))}</p>
                                            <h6>{t(payment_stage_label(rowItem.paymentStage))}</h6>
                                        </td>
                                        <td><p>{format_date_time(rowItem.confirmedAt)}</p></td>
                                        <td><h5>{format_money(rowItem.amountNet, rowItem.currency)}</h5></td>
                                    </tr>
                                ))}
                            </tbody>
                        </GoalsTable>
                    </GoalsTableWrap>
                ) : null}
            </GoalsContent>
        </PageSidebar>
    );
}
